/**
 * #490 AI Emotion Wave（AI 情绪波形）
 * 
 * 设计灵感：2026 AI 趋势 - AI 情绪可视化
 * 来源：2026 AI 趋势 - AI 情绪感知成为主流
 * 
 * 类型：情绪波形 + AI 可视化 + 动态颜色
 * 
 * 核心技术：
 * - 情绪波形动画（5 种情绪：开心、悲伤、愤怒、恐惧、惊讶）
 * - 动态颜色变化（基于情绪，HSL 色相旋转）
 * - 波形粒子系统（40 个粒子，沿波形运动）
 * - Spring 物理动画（平滑入场）
 * - 情绪标签指示器（5 个圆形指示器）
 * - 波形强度变化（amplitude 动画）
 * - 背景情绪网格（渐变网格）
 * - 情绪统计面板（5 种情绪的占比）
 * 
 * 视觉效果：情感化、AI 感、科技感、现代感
 * 
 * 应用场景：
 * - AI 情绪感知产品演示
 * - 情感计算教程
 * - 心理健康应用
 * - AI 聊天机器人展示
 * - 小红书 AI 主题视频
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// 情绪类型定义
interface Emotion {
  name: string;        // 情绪名称
  emoji: string;       // 情绪 emoji
  color: string;       // 情绪颜色（HSL）
  frequency: number;   // 波形频率
  amplitude: number;   // 波形振幅
  phase: number;       // 波形相位
  percentage: number;  // 占比百分比
}

// 粒子类型定义
interface Particle {
  x: number;           // x 坐标
  y: number;           // y 坐标
  color: string;       // 颜色
  size: number;        // 大小
  speed: number;       // 速度
  offset: number;      // 偏移量
}

// 5 种情绪数据
const emotions: Emotion[] = [
  { name: '开心', emoji: '😊', color: 'hsl(45, 100%, 60%)', frequency: 0.08, amplitude: 80, phase: 0, percentage: 35 },
  { name: '悲伤', emoji: '😢', color: 'hsl(220, 80%, 50%)', frequency: 0.12, amplitude: 60, phase: Math.PI / 3, percentage: 20 },
  { name: '愤怒', emoji: '😠', color: 'hsl(0, 100%, 50%)', frequency: 0.15, amplitude: 100, phase: Math.PI / 2, percentage: 15 },
  { name: '恐惧', emoji: '😨', color: 'hsl(280, 80%, 50%)', frequency: 0.1, amplitude: 70, phase: Math.PI, percentage: 18 },
  { name: '惊讶', emoji: '😲', color: 'hsl(30, 100%, 60%)', frequency: 0.14, amplitude: 90, phase: Math.PI * 1.5, percentage: 12 },
];

// 确定性随机函数（基于帧和种子）
const seededRandom = (seed: number, frame: number): number => {
  const x = Math.sin(seed + frame * 0.1) * 10000;
  return x - Math.floor(x);
};

// AIEmotionWave 组件
export const AIEmotionWave: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // 当前情绪索引（每 30 帧切换一次）
  const currentEmotionIndex = Math.floor(frame / 30) % emotions.length;
  const currentEmotion = emotions[currentEmotionIndex];

  // Spring 物理进入动画
  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 200, stiffness: 100 },
  });

  const waveSpring = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });

  // 生成波形路径
  const generateWavePath = (emotion: Emotion, offset: number = 0): string => {
    const points: string[] = [];
    const centerY = height / 2;
    const waveWidth = width * 0.8;
    const startX = width * 0.1;

    for (let x = startX; x <= startX + waveWidth; x += 5) {
      // 波形方程：y = amplitude * sin(frequency * (x + frame) + phase)
      const progress = (x - startX) / waveWidth;
      const waveY = centerY + 
        emotion.amplitude * 
        Math.sin(emotion.frequency * (x + frame * 2 + offset) + emotion.phase) *
        Math.sin(progress * Math.PI); // 两端衰减

      points.push(`${x},${waveY}`);
    }

    return `M ${points.join(' L ')}`;
  };

  // 生成粒子系统
  const particles: Particle[] = [];
  for (let i = 0; i < 40; i++) {
    const seed = i * 1000;
    const angle = seededRandom(seed, frame) * Math.PI * 2;
    const radius = 250 + seededRandom(seed + 1, frame) * 150;
    const centerX = width / 2;
    const centerY = height / 2;

    // 粒子沿波形运动
    const waveX = width * 0.1 + seededRandom(seed + 2, frame) * width * 0.8;
    const waveY = centerY + 
      currentEmotion.amplitude * 
      Math.sin(currentEmotion.frequency * (waveX + frame * 2) + currentEmotion.phase);

    particles.push({
      x: waveX,
      y: waveY,
      color: currentEmotion.color,
      size: 3 + seededRandom(seed + 3, frame) * 5,
      speed: 1 + seededRandom(seed + 4, frame) * 2,
      offset: seededRandom(seed + 5, frame) * 100,
    });
  }

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(circle at center, 
          ${currentEmotion.color.replace('60%', '30%').replace('50%', '25%')} 0%, 
          #0a0a0f 70%)`,
      }}
    >
      {/* 背景情绪网格 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          opacity: 0.2,
          transform: `rotate(${frame * 0.2}deg)`,
        }}
      >
        <defs>
          <pattern
            id="emotionGrid"
            x="0"
            y="0"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke={currentEmotion.color}
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width={width} height={height} fill="url(#emotionGrid)" />
      </svg>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '50%',
          transform: `translateX(-50%) translateY(${interpolate(titleSpring, [0, 1], [-50, 0])}px)`,
          opacity: titleSpring,
          textAlign: 'center',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div
          style={{
            fontSize: 90,
            fontWeight: 'bold',
            color: currentEmotion.color,
            textShadow: `0 0 30px ${currentEmotion.color}`,
            marginBottom: 20,
          }}
        >
          AI 情绪波形
        </div>
        <div
          style={{
            fontSize: 50,
            color: 'white',
            opacity: 0.8,
          }}
        >
          当前情绪：{currentEmotion.emoji} {currentEmotion.name}
        </div>
      </div>

      {/* 情绪波形 */}
      <svg
        width={width}
        height={height}
        style={{
          position: 'absolute',
          opacity: waveSpring,
          transform: `scale(${waveSpring})`,
        }}
      >
        <defs>
          {/* 主波形发光效果 */}
          <filter id="waveGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* 次级发光 */}
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 5 种情绪波形 */}
        {emotions.map((emotion, index) => {
          const isActive = index === currentEmotionIndex;
          const opacity = isActive ? 1 : 0.3;
          const delay = index * 0.1;
          
          return (
            <g key={emotion.name}>
              {/* 波形填充区域 */}
              <path
                d={generateWavePath(emotion, index * 20)}
                fill="none"
                stroke={emotion.color}
                strokeWidth={isActive ? 4 : 2}
                strokeOpacity={opacity}
                filter={isActive ? 'url(#waveGlow)' : 'url(#softGlow)'}
                style={{
                  transform: `translateY(${(index - 2) * 30}px)`,
                }}
              />
            </g>
          );
        })}
      </svg>

      {/* 粒子系统 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {particles.map((particle, index) => (
          <circle
            key={index}
            cx={particle.x}
            cy={particle.y}
            r={particle.size}
            fill={particle.color}
            opacity={0.6}
            filter="url(#softGlow)"
          />
        ))}
      </svg>

      {/* 情绪标签指示器 */}
      <div
        style={{
          position: 'absolute',
          top: '65%',
          left: '50%',
          transform: `translateX(-50%) translateY(${interpolate(waveSpring, [0, 1], [50, 0])}px)`,
          opacity: waveSpring,
          display: 'flex',
          gap: 40,
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {emotions.map((emotion, index) => {
          const isActive = index === currentEmotionIndex;
          return (
            <div
              key={emotion.name}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                marginRight: 30,
              }}
            >
              {/* 情绪指示器 */}
              <div
                style={{
                  width: isActive ? 60 : 40,
                  height: isActive ? 60 : 40,
                  borderRadius: '50%',
                  background: emotion.color,
                  boxShadow: isActive
                    ? `0 0 30px ${emotion.color}, 0 0 60px ${emotion.color}`
                    : `0 0 15px ${emotion.color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isActive ? 80 : 70,
                  transition: 'all 0.3s ease',
                  marginBottom: 20
                }}
              >
                {emotion.emoji}
              </div>
              
              {/* 情绪名称和占比 */}
              <div
                style={{
                  color: isActive ? 'white' : 'rgba(255,255,255,0.5)',
                  fontSize: isActive ? 40 : 38,
                  fontWeight: isActive ? 'bold' : 'normal',
                  textAlign: 'center',
                }}
              >
                {emotion.name}
                <br />
                {emotion.percentage}%
              </div>
            </div>
          );
        })}
      </div>

      {/* 底部信息面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 40,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: 15,
          padding: '20px 30px',
          fontFamily: 'Arial, sans-serif',
          border: `2px solid ${currentEmotion.color}`,
          opacity: waveSpring,
        }}
      >
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 34, marginBottom: 8 }}>
          AI 情绪分析系统
        </div>
        <div style={{ color: 'white', fontSize: 34, fontWeight: 'bold', marginBottom: 4 }}>
          当前情绪：{currentEmotion.emoji} {currentEmotion.name}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 36 }}>
          置信度：{85 + Math.sin(frame * 0.1) * 10}%
        </div>
      </div>
    </AbsoluteFill>
  );
};
